import dayjs from "dayjs";
import PropTypes from "prop-types";
import { FormProvider, useForm } from "react-hook-form";

import { yupResolver } from "@hookform/resolvers/yup";
import { Divider, Paper, Stack } from "@mui/material";
import Grid from "@mui/material/Grid";

import { CenteredLoading } from "../../ui";
import { GeneralInformationSection } from "./general-information-section/general-information-section";
import { OtherSection } from "./other-section/other-section";
import { PriceSummarySection } from "./price-summary-section/price-summary-section";
import { PurchaseInvoicePaymentsSection } from "./purchase-invoice-payments/purchase-invoice-payments";
import {
  DEFAULT_VALUES,
  VALIDATION_SCHEMA,
} from "./purchase-payment-form-constants";
import { SelectOutstandingPayableSection } from "./select-outstanding-payable-section";

export const PurchasePaymentForm = ({
  defaultValues,
  onSubmit,
  renderActionButton,
  editMode = false,
  isLoading = false,
  // A purchase payment that has been converted into a sales payment accepts a
  // header-only edit: the mirrored document depends on its allocations and
  // discount rows staying exactly as they were at conversion time.
  headerOnly = false,
}) => {
  const methods = useForm({
    defaultValues: defaultValues
      ? { ...DEFAULT_VALUES, ...defaultValues }
      : DEFAULT_VALUES,
    resolver: yupResolver(VALIDATION_SCHEMA),
  });
  const { handleSubmit, formState } = methods;

  const onFormSubmit = async (formData) => {
    const payload = {
      transactionDate: dayjs(formData.transactionDate).format("YYYY-MM-DD"),
      buyerId: formData.buyer.id,
      sellerableId: formData.seller.id,
      sellerableType: formData.sellerableType,
      cashBankOutletChartOfAccountId: formData.outletChartOfAccount.id,
      note: formData.note || null,
      // Sending either of these against a converted payment is rejected
      // outright, so they are omitted rather than sent unchanged.
      ...(headerOnly
        ? {}
        : {
            grandTotal: formData.grandTotal,
            purchaseInvoicePayments: formData.purchaseInvoicePayments.map(
              (payment) => ({
                purchaseInvoiceId: payment.purchaseInvoiceId,
                allocatedAmount: payment.allocatedAmount,
                discountAccounts: (payment.discountAccounts ?? []).map(
                  (account) => ({
                    outletChartOfAccountId: account.outletChartOfAccount.id,
                    amount: account.amount,
                    note: account.note || null,
                  })
                ),
              })
            ),
          }),
    };

    await onSubmit(payload);
  };

  return isLoading ? (
    <CenteredLoading sx={{ height: "100vh" }} />
  ) : (
    <FormProvider {...methods}>
      <Grid
        container
        component="form"
        noValidate
        onSubmit={handleSubmit(onFormSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") {
            e.preventDefault();
          }
        }}
        spacing={2.5}
      >
        <Grid size={{ xs: 12 }}>
          <GeneralInformationSection editMode={editMode} />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Paper sx={{ p: 2.5, borderRadius: 2 }}>
            <Stack spacing={2.5}>
              {!headerOnly && (
                <>
                  <SelectOutstandingPayableSection />
                  <Divider />
                </>
              )}
              <PurchaseInvoicePaymentsSection readOnly={headerOnly} />
            </Stack>
          </Paper>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 7 }}>
          <OtherSection />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, lg: 5 }}>
          <PriceSummarySection />
        </Grid>
        <Grid
          size={{ xs: 12 }}
          sx={{
            display: "flex",
            justifyContent: "flex-end"
          }}>
          {renderActionButton({ isSubmitting: formState.isSubmitting })}
        </Grid>
      </Grid>
    </FormProvider>
  );
};

PurchasePaymentForm.propTypes = {
  defaultValues: PropTypes.object,
  onSubmit: PropTypes.func,
  renderActionButton: PropTypes.func,
  editMode: PropTypes.bool,
  isLoading: PropTypes.bool,
  headerOnly: PropTypes.bool,
};
